import type {
  CapabilityGrantResponse,
  CapabilityLevel,
  DataScope,
} from "@kontextwerk/control-web-client"
import type { CapabilityDefinition } from "@kontextwerk/control-web-frame/shared/logic/authorization"
import { KEHRBLATT_CONTEXT_CATALOG } from "../../../../_shared/_logic/context-capabilities"

/** Every key of the KB capability catalog (Core catalog plus app/config/capability_catalog.py). */
export type PermissionKey =
  | "core.notes"
  | "core.projects"
  | "core.memberships"
  | "core.roles"
  | "core.org_settings"
  | "core.assistant_configuration"
  | "core.billing"
  | "core.communication_channels"
  | "core.contact_orders"
  | "core.activity"
  | "core.audit_log"
  | "core.analytics"
  | "core.notifications"
  | "core.calendar_integrations"
  | "core.kontext_index"
  | "core.kontext_answer_library"
  | "core.kontext_file_upload"
  | "core.kontext_website_index"
  | "core.kontext_sharepoint"
  | "core.werk_sessions"
  | "core.werk_telephony"
  | "kehrblatt.scheduling"
  | "kehrblatt.registry"
  | "kehrblatt.demo_reset"

export type PermissionLevel = CapabilityLevel
export type PermissionScope = DataScope

export type LevelOption = { value: PermissionLevel; name: string }
export type ScopeOption = { value: PermissionScope; name: string }

export type PermissionRowConfig<K extends string = string> = {
  key: K
  label: string
  levelOptions: LevelOption[]
  scopeOptions?: ScopeOption[]
  description?: string
  levelTooltip?: string
  scopeTooltip?: string
}

export type PermissionSectionConfig<K extends string = string> = {
  id: string
  title: string
  description?: string | null
  helpDialogId?: string | null
  helpDialogTooltip?: string | null
  rows: PermissionRowConfig<K>[]
}

/** Grid draft of one complete grant set; keys are catalog keys, never role names. */
export type PermissionGridState = {
  levels: Record<string, PermissionLevel>
  scopes: Record<string, PermissionScope | null>
}

type RowCopy = Omit<PermissionRowConfig<PermissionKey>, "levelOptions" | "scopeOptions"> & {
  scopeNames?: Partial<Record<PermissionScope, string>>
}

type SectionCopy = Omit<PermissionSectionConfig<PermissionKey>, "rows"> & { rows: RowCopy[] }

const LEVEL_NAMES: Record<PermissionLevel, string> = {
  none: "Kein Zugriff",
  view: "Ansehen",
  manage: "Verwalten",
}

const SCOPE_NAMES: Record<PermissionScope, string> = {
  own: "Eigene",
  all: "Alle",
}

const SECTION_COPY: SectionCopy[] = [
  {
    id: "core",
    title: "Basisplattform",
    rows: [
      {
        key: "core.projects",
        label: "Agenten",
        description:
          "Steuert den Zugriff auf Agenten und deren Projekteinstellungen. „Ansehen“ zeigt Agenten und Projekte. „Verwalten“ erlaubt Konfigurationen, Änderungen und riskante Aktionen wie Löschen.",
      },
      {
        key: "core.memberships",
        label: "Benutzer",
        description:
          "Steuert den Zugriff auf Organisations-Benutzer. „Ansehen“ zeigt Mitglieder. „Verwalten“ erlaubt Einladen, Sperren und Entsperren sowie das Zuweisen von Rollen.",
      },
      {
        key: "core.roles",
        label: "Rollen & Berechtigungen",
        description:
          "Steuert den Zugriff auf Rollen und ihre Berechtigungen. „Ansehen“ zeigt Rollen und ihre Rechte. „Verwalten“ erlaubt das Bearbeiten eigener Rollen und beeinflusst damit die Berechtigungen anderer Benutzer.",
      },
      {
        key: "core.org_settings",
        label: "Organisation",
        description: "Zugriff auf Organisationsdaten und Kehrblatt-Einstellungen.",
        levelTooltip:
          "In Kehrblatt umfasst Organisation unter anderem allgemeine Einstellungen, Auftragstypen und je nach Tarif/Rolle Kontaktprofile.",
      },
      {
        key: "core.assistant_configuration",
        label: "Sitzungsdetails & KI-Regeln",
        description:
          "Zugriff auf Sitzungsdetails, Antwortregeln und Übergaberegeln für KI-Sessions. Secrets bleiben unabhängig davon nur für System-Admins sichtbar.",
      },
      {
        key: "core.billing",
        label: "Abrechnung & Abo",
        description: "Zugriff auf Abrechnung, Abo und abrechnungs- oder rechtliche Dokumente.",
        levelTooltip:
          "Dokumente haben keine eigene Berechtigung. Sie hängen an Abrechnung & Abo und zusätzlich an der Verfügbarkeit der Abrechnung.",
      },
      {
        key: "core.communication_channels",
        label: "Kommunikationskanäle",
        description:
          "Steuert den Zugriff auf Telefonnummern und weitere Kommunikationskanäle der Organisation. „Verwalten“ erlaubt das Einrichten und Ändern der Kanäle.",
      },
      {
        key: "core.notes",
        label: "Notizen",
        description:
          "Steuert den Zugriff auf Notizen an Einträgen, die ohnehin sichtbar sind. „Verwalten“ erlaubt Notizen anzulegen, zu ändern und zu löschen.",
      },
      {
        key: "core.activity",
        label: "Verlauf",
        description:
          "Erlaubt das Einsehen des Verlaufs von Einträgen, auf die ohnehin Zugriff besteht. Das Aktivitätsprotokoll der Organisation bleibt eine eigene Berechtigung.",
      },
      {
        key: "core.audit_log",
        label: "Aktivitätsprotokoll",
        description:
          "Erlaubt das Einsehen des Aktivitätsprotokolls mit wichtigen Aktionen und Änderungen. Dieses Recht ist nur lesend.",
      },
      {
        key: "core.analytics",
        label: "KI-Session-Auswertung",
        description:
          "Steuert den Zugriff auf Statistiken und Auswertungen zu KI-Sessions. Dieses Recht öffnet Auswertungsansichten, aber keine Roh-Konversationen.",
      },
      {
        key: "core.notifications",
        label: "Mitteilungen",
        scopeNames: { own: "Eigene Mitteilungen", all: "Alle Mitteilungen" },
        description: "Steuert den Zugriff auf Mitteilungen wie Aufgaben, Warnungen und Hinweise.",
        levelTooltip:
          "„Ansehen“ erlaubt Mitteilungen zu lesen. „Verwalten“ erlaubt zusätzlich Mitteilungen zu erledigen oder als gelesen zu markieren.",
        scopeTooltip:
          "Der Umfang „Eigene Mitteilungen“ gilt für eigene und unzugeordnete Einträge. „Alle Mitteilungen“ umfasst die gesamte Organisation.",
      },
      {
        key: "core.calendar_integrations",
        label: "Kalenderanbindungen",
        scopeNames: { own: "Eigene Kalenderanbindungen", all: "Alle Kalenderanbindungen" },
        description:
          "Steuert den Zugriff auf verbundene Kalender der Mitglieder. „Verwalten“ erlaubt das Trennen von Kalenderanbindungen.",
        scopeTooltip:
          "„Eigene Kalenderanbindungen“ gilt nur für die eigenen Verbindungen. „Alle Kalenderanbindungen“ umfasst alle Mitglieder der Organisation.",
      },
      {
        key: "core.contact_orders",
        label: "Kontaktaufträge",
        description:
          "Steuert den Zugriff auf Kontaktaufträge der Basisplattform. In Kehrblatt werden Kontaktpläne zusätzlich über „Aufträge & Termine“ freigegeben.",
      },
    ],
  },
  {
    id: "kontextkern",
    title: "KontextKern",
    description: "Zugriff auf Wissensindex, FAQ-Sammlung, Dateien, Website-Inhalte und SharePoint.",
    helpDialogId: "userPermissionsKontextkern",
    helpDialogTooltip: "So funktionieren KontextKern-Berechtigungen",
    rows: [
      {
        key: "core.kontext_index",
        label: "Wissensindex",
        description:
          "„Ansehen“ erlaubt den Zugriff auf die Wissensindex-Übersicht inklusive Datenquellen und Chunk Explorer. Ohne Zugriff wird der Chunk Explorer vollständig ausgeblendet.",
      },
      {
        key: "core.kontext_answer_library",
        label: "FAQ-Sammlung",
        description:
          "Steuert den Zugriff auf die FAQ-Sammlung. „Ansehen“ erlaubt lesenden Zugriff. „Verwalten“ erlaubt Anlegen, Ändern und Löschen von Einträgen.",
      },
      {
        key: "core.kontext_file_upload",
        label: "Dateien",
        description:
          "Steuert den Zugriff auf Dateien im Wissensbereich. „Ansehen“ erlaubt den lesenden Überblick. „Verwalten“ erlaubt Uploads und Änderungen.",
      },
      {
        key: "core.kontext_website_index",
        label: "Website-Inhalte",
        description:
          "Steuert den Zugriff auf Website-Quellen und deren Indexierung. „Ansehen“ erlaubt lesenden Zugriff. „Verwalten“ erlaubt Domains und URLs anzulegen, zu ändern und zu löschen.",
      },
      {
        key: "core.kontext_sharepoint",
        label: "SharePoint",
        description:
          "Steuert den Zugriff auf SharePoint-Quellen und deren Synchronisierung. „Ansehen“ erlaubt lesenden Zugriff auf Quellen, Bestand und Jobs. „Verwalten“ erlaubt Anlegen, Ändern, Löschen und manuelle Synchronisationen.",
      },
    ],
  },
  {
    id: "werkadmin",
    title: "KI-Sessions & Telefonie",
    description: "Zugriff auf KI-Sessions und Telefonie-Funktionen.",
    helpDialogId: "userPermissionsWerkadmin",
    helpDialogTooltip: "So funktionieren KI-Session- und Telefonie-Berechtigungen",
    rows: [
      {
        key: "core.werk_sessions",
        label: "KI-Sessions",
        scopeNames: { all: "Alle KI-Sessions" },
        description:
          "Steuert den Zugriff auf KI-Sessions. „Ansehen“ erlaubt lesenden Zugriff. „Verwalten“ erlaubt zusätzlich schreibende Aktionen wie Annotationen. Der Umfang „Alle KI-Sessions“ gilt für die gesamte Organisation.",
      },
      {
        key: "core.werk_telephony",
        label: "Telefonie",
        description: "Steuert den Zugriff auf Telefonie-Funktionen wie Dialer und Outbound-Calls.",
      },
    ],
  },
  {
    id: "termin",
    title: "Kehrblatt Control",
    description: "Aufträge, Termine, Kalender und Kehrbezirk.",
    helpDialogId: "userPermissionsKehrblattControl",
    helpDialogTooltip: "So funktionieren Kehrblatt-Control-Berechtigungen",
    rows: [
      {
        key: "kehrblatt.scheduling",
        label: "Aufträge & Termine",
        scopeNames: { own: "Eigener Arbeitsbereich", all: "Gesamte Organisation" },
        description: "Steuert gemeinsam den Zugriff auf Aufträge, Termine und Kalender.",
        scopeTooltip:
          "Zum eigenen Arbeitsbereich gehören direkt zugewiesene Aufträge sowie vollständige Auftragsverbünde, sobald mindestens ein weiterhin verknüpfter Termin – auch ein historischer – dem Mitarbeiter zugewiesen ist. Freie Kalenderblöcke gehören nur bei ausdrücklicher Zuweisung dazu.",
      },
      {
        key: "kehrblatt.registry",
        label: "Kehrbezirk",
        description:
          "Steuert den Zugriff auf Kehrbezirk, Kunden und Objekte. „Ansehen“ erlaubt lesenden Zugriff. „Verwalten“ erlaubt Anlegen, Ändern, Importieren und Archivieren.",
      },
      {
        key: "kehrblatt.demo_reset",
        label: "Testzugang",
        description:
          "Erlaubt das Zurücksetzen eines Testzugangs. Dabei werden alle Testdaten gelöscht und neu erstellt.",
      },
    ],
  },
]

const definitionsByKey = new Map<string, CapabilityDefinition>(
  KEHRBLATT_CONTEXT_CATALOG.map((definition) => [definition.key, definition]),
)

const requireDefinition = (key: string): CapabilityDefinition => {
  const definition = definitionsByKey.get(key)

  if (!definition) throw new Error(`permission_section_key_not_in_catalog: ${key}`)

  return definition
}

/** Options follow the catalog definition, so a grid can never offer an undeclared grant. */
const buildRow = ({ scopeNames, ...copy }: RowCopy): PermissionRowConfig<PermissionKey> => {
  const definition = requireDefinition(copy.key)
  const levelOptions: LevelOption[] = [
    { value: "none", name: LEVEL_NAMES.none },
    ...definition.levels.map((level) => ({ value: level, name: LEVEL_NAMES[level] })),
  ]
  const scopeOptions: ScopeOption[] = definition.scopes.map((scope) => ({
    value: scope,
    name: scopeNames?.[scope] ?? SCOPE_NAMES[scope],
  }))

  return { ...copy, levelOptions, ...(scopeOptions.length ? { scopeOptions } : {}) }
}

export const PERMISSION_SECTIONS: PermissionSectionConfig<PermissionKey>[] = SECTION_COPY.map(
  ({ rows, ...section }) => ({ ...section, rows: rows.map(buildRow) }),
)
export const APP_PERMISSION_SECTIONS: PermissionSectionConfig<PermissionKey>[] =
  PERMISSION_SECTIONS.filter((section) => section.id === "termin")
export const INVITE_PERMISSION_SECTIONS: PermissionSectionConfig<PermissionKey>[] =
  PERMISSION_SECTIONS.filter((section) => ["core", "termin"].includes(section.id))
export const PERMISSION_KEYS: PermissionKey[] = PERMISSION_SECTIONS.flatMap((section) =>
  section.rows.map((row) => row.key),
)
/** Catalog definitions in presentation order. */
export const PERMISSION_DEFINITIONS: readonly CapabilityDefinition[] =
  PERMISSION_KEYS.map(requireDefinition)

const allowedScope = (
  definition: CapabilityDefinition,
  scope: PermissionScope | null | undefined,
): PermissionScope | null => (scope && definition.scopes.includes(scope) ? scope : null)

/** Grid state of a stored grant set; absent or unknown keys show "Kein Zugriff". */
export const permissionStateFromGrants = (
  grants: readonly CapabilityGrantResponse[],
): PermissionGridState => {
  const byKey = new Map(grants.map((grant) => [grant.key, grant]))
  const state: PermissionGridState = { levels: {}, scopes: {} }
  for (const definition of KEHRBLATT_CONTEXT_CATALOG) {
    const grant = byKey.get(definition.key)
    const level: PermissionLevel =
      grant && grant.level !== "none" && definition.levels.includes(grant.level)
        ? grant.level
        : "none"
    state.levels[definition.key] = level
    state.scopes[definition.key] =
      level === "none"
        ? (definition.scopes[0] ?? null)
        : (allowedScope(definition, grant?.scope) ?? definition.scopes[0] ?? null)
  }

  return state
}

/**
 * Complete grant list for a role replacement: every catalog key exactly once, scopes only where
 * the catalog declares them and never on "Kein Zugriff". The server stays the validating owner.
 */
export const grantsFromPermissionState = (state: PermissionGridState): CapabilityGrantResponse[] =>
  KEHRBLATT_CONTEXT_CATALOG.map((definition) => {
    const requested = state.levels[definition.key]
    const level: PermissionLevel =
      requested && requested !== "none" && definition.levels.includes(requested)
        ? requested
        : "none"

    if (level === "none") return { key: definition.key, level, scope: null }

    return {
      key: definition.key,
      level,
      scope: definition.scopes.length
        ? (allowedScope(definition, state.scopes[definition.key]) ?? definition.scopes[0])
        : null,
    }
  })

export const permissionStatesEqual = (
  left: PermissionGridState,
  right: PermissionGridState,
): boolean =>
  JSON.stringify(grantsFromPermissionState(left)) ===
  JSON.stringify(grantsFromPermissionState(right))

export const permissionLevelName = (level: PermissionLevel): string => LEVEL_NAMES[level]
